import React, { useContext, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { WebTemplateContext } from "../context/WebTemplateContext";
import { PlatformContext } from "../context/PlatformContext";
import { getMLField } from "../utils/multilanguage";

export function getAnnouncementText(webTemplate, currentLanguage, defaultLanguage) {
	const localized = getMLField(
		webTemplate,
		"announcementText",
		currentLanguage,
		defaultLanguage
	);
	if (localized) return localized;

	let ml = webTemplate?.announcementText_ml;
	if (typeof ml === "string") {
		try {
			ml = JSON.parse(ml);
		} catch {
			ml = null;
		}
	}
	if (ml && typeof ml === "object") {
		const fallback =
			ml[defaultLanguage] ||
			ml.es ||
			Object.values(ml).find((value) => typeof value === "string" && value.trim());
		if (fallback) return String(fallback);
	}

	const flat = webTemplate?.announcementText;
	return flat != null && String(flat).trim() ? String(flat) : "";
}

export default function AnnouncementBar() {
	const { webTemplate } = useContext(WebTemplateContext);
	const { settings } = useContext(PlatformContext);
	const { i18n } = useTranslation();
	const barRef = useRef(null);

	const currentLanguage = i18n.language || "es";
	const defaultLanguage = settings?.defaultLanguage || "es";
	const text = getAnnouncementText(
		webTemplate,
		currentLanguage,
		defaultLanguage
	);
	const sectionHidden =
		Array.isArray(webTemplate?.hiddenSections) &&
		webTemplate.hiddenSections.includes("announcement");
	const visible = Boolean(
		webTemplate?.announcementEnabled && text && !sectionHidden
	);

	useEffect(() => {
		const root = document.documentElement;
		if (!visible) {
			root.classList.remove("has-announcement-bar");
			root.style.removeProperty("--announcement-offset");
			return undefined;
		}

		const applyHeight = () => {
			const height = barRef.current?.offsetHeight || 40;
			root.classList.add("has-announcement-bar");
			root.style.setProperty("--announcement-offset", `${height}px`);
		};

		applyHeight();
		window.addEventListener("resize", applyHeight);
		return () => {
			window.removeEventListener("resize", applyHeight);
			root.classList.remove("has-announcement-bar");
			root.style.removeProperty("--announcement-offset");
		};
	}, [visible, text]);

	if (!visible) return null;

	const bg = webTemplate?.announcementBgColor || "#000000";
	const color = webTemplate?.announcementTextColor || "#ffffff";
	const href = webTemplate?.announcementLink;

	return (
		<div
			ref={barRef}
			className="announcement-bar fixed top-0 left-0 right-0 w-full text-center py-2.5 px-4 text-sm font-medium z-[70]"
			style={{ backgroundColor: bg, color }}
		>
			{href ? (
				<a
					href={href}
					target="_blank"
					rel="noopener noreferrer"
					className="hover:opacity-80 transition-opacity"
					style={{ color }}
				>
					{text}
				</a>
			) : (
				<span>{text}</span>
			)}
		</div>
	);
}
